import type { AuthUser } from '@pixelworld/shared';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { Field, FormError } from '../../components/Field';
import { da } from '../../i18n/da';
import { renamePlayer } from './profile-api';
import styles from './ProfilePage.module.css';

interface DisplayNameFormProps {
  user: AuthUser;
  onRenamed: (user: AuthUser) => void;
}

/** The name the others see. The server checks it against the name filter and other players. */
export function DisplayNameForm({ user, onRenamed }: DisplayNameFormProps) {
  const [name, setName] = useState(user.displayName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const trimmed = name.trim();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    const result = await renamePlayer({ displayName: trimmed });
    setBusy(false);
    if (!result.ok) {
      setError(da.authErrors[result.error]);
      return;
    }
    setSaved(true);
    onRenamed(result.data.user);
  };

  return (
    <form
      className={styles.section}
      aria-labelledby="name-title"
      onSubmit={(event) => void handleSubmit(event)}
    >
      <h3 id="name-title" className={styles.sectionTitle}>
        {da.profile.nameTitle}
      </h3>
      <div className={styles.nameRow}>
        <Field
          id="display-name"
          label={da.profile.nameLabel}
          hint={da.profile.nameHint(user.username)}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
          maxLength={16}
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          type="submit"
          variant="secondary"
          disabled={busy || trimmed.length === 0 || trimmed === user.displayName}
        >
          {da.profile.saveName}
        </Button>
      </div>
      <FormError message={error} />
      {saved && (
        <p className={styles.saved} role="status">
          {da.profile.nameSaved}
        </p>
      )}
    </form>
  );
}
